import { Table } from '@tanstack/react-table';

/**
 * Brings a freshly added table row to the user: expands it so its lines are visible and
 * smooth-scrolls the table to it. Without this, a row appended below the fold is added
 * silently and most users never notice it.
 *
 * Scrolls only the table's own scroll container (`data-scroll-container`), never the page
 * around it. Runs on the next frame because callers invoke it from the effect that reacts to
 * the table data rebuild — the `<tr>` exists in the DOM by then, but its layout has not settled.
 */
export const revealTableRow = <T>(table: Table<T>, rowId: string): void => {
  const row = table.getCoreRowModel().rowsById[rowId];
  if (!row) return;

  if (row.getCanExpand() && !row.getIsExpanded()) {
    row.toggleExpanded(true);
  }

  requestAnimationFrame(() => {
    const element = document.querySelector<HTMLTableRowElement>(
      `tr[data-row-id="${CSS.escape(rowId)}"]`
    );
    const container = element?.closest<HTMLElement>('[data-scroll-container]');
    if (!element || !container) return;

    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const offsetInContainer = elementRect.top - containerRect.top + container.scrollTop;
    const centeredTop = offsetInContainer - (container.clientHeight - elementRect.height) / 2;

    container.scrollTo({ top: Math.max(0, centeredTop), behavior: 'smooth' });
  });
};
